'use client';

import { useState } from 'react';

import { client } from '../../../utils/trpc';
import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
import { diagnosis, DILEMMAS } from './dilemmaData';

const BEST = bestKey('dilemmas');

/**
 * The crowd split for one dilemma. `pending` while the vote is in flight and
 * `failed` when it could not be recorded — a failure shows nothing rather than
 * falling back to an invented number.
 */
type Crowd =
  | { state: 'pending' }
  | { state: 'failed' }
  | { state: 'ready'; pulledPercent: number; total: number };

const Dilemmas = () => {
  const [index, setIndex] = useState(0);
  const [choices, setChoices] = useState<boolean[]>([]);
  /** Crowd result per dilemma index, so going Next and back never refetches. */
  const [crowd, setCrowd] = useState<Record<number, Crowd>>({});

  const best = useLocalBest(BEST);
  const { record } = useGameTracking();

  const finished = index >= DILEMMAS.length;
  const current = DILEMMAS[index];
  // A choice for the current card exists exactly when we've recorded past it.
  const revealed = !finished && choices.length > index;
  const pulledCount = choices.filter(Boolean).length;

  const choose = async (pulled: boolean) => {
    if (revealed) return;

    const at = index;
    setChoices([...choices, pulled]);
    setCrowd((prev) => ({ ...prev, [at]: { state: 'pending' } }));

    // Errors are surfaced inline as "Could not reach the crowd", so the global
    // error toast would just say the same thing twice, over the game.
    const [error, data] = await client.arcade.voteDilemma.mutate(
      { dilemmaId: DILEMMAS[at].id, pulled },
      { context: { silenceErrors: true } }
    );

    setCrowd((prev) => ({
      ...prev,
      [at]:
        error || !data
          ? { state: 'failed' }
          : {
              state: 'ready',
              pulledPercent: data.pulledPercent,
              total: data.total
            }
    }));
  };

  const next = () => {
    const nextIndex = index + 1;
    setIndex(nextIndex);

    if (nextIndex >= DILEMMAS.length) {
      record('dilemmas', choices.filter(Boolean).length, {
        answered: choices.length
      });
    }
  };

  const restart = () => {
    setIndex(0);
    setChoices([]);
    setCrowd({});
  };

  if (finished) {
    return (
      <div className="rounded-2xl border border-ds-border-standard bg-background-secondary p-6 text-center">
        <p className="text-sm uppercase tracking-wide text-textGray">
          Final tally
        </p>
        <p className="mt-2 text-5xl font-bold tabular-nums text-textPrimary">
          {pulledCount}/{DILEMMAS.length}
        </p>
        <p className="mt-1 text-textSecondary">levers pulled</p>
        <p className="mx-auto mt-4 max-w-sm text-textPrimary">
          {diagnosis(pulledCount, DILEMMAS.length)}
        </p>

        <button
          type="button"
          onClick={restart}
          className="mt-6 rounded-full border border-ds-border-standard px-5 py-2 text-sm font-emphasis text-textPrimary transition-colors hover:bg-background-light"
        >
          Go again
        </button>

        {best !== null && (
          <p className="mt-4 text-sm text-textGray">
            Most levers you have pulled in one run: {best}/{DILEMMAS.length}.
          </p>
        )}
      </div>
    );
  }

  const chosePull = revealed && choices[index];
  const thisCrowd = crowd[index];

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-light">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300 ease-in-out"
            style={{ width: `${(index / DILEMMAS.length) * 100}%` }}
          />
        </div>
        <span className="text-sm tabular-nums text-textGray">
          {index + 1}/{DILEMMAS.length}
        </span>
      </div>

      <div className="rounded-2xl border border-ds-border-standard bg-background-secondary p-6">
        <p className="text-lg leading-relaxed text-textPrimary">
          {current.scenario}
        </p>

        {!revealed ? (
          <div className="mt-6 flex flex-col gap-3">
            {[
              { label: current.pull, pulled: true },
              { label: current.nothing, pulled: false }
            ].map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => {
                  void choose(option.pulled);
                }}
                className="rounded-xl border border-ds-border-standard bg-background-primary p-4 text-left font-emphasis text-textPrimary transition-all duration-200 hover:-translate-y-0.5 hover:border-ds-border-hover hover:bg-background-light"
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="animate-fadeIn mt-6">
            <p className="mb-3 text-sm text-textSecondary">
              You chose to{' '}
              <span className="font-emphasis text-textPrimary">
                {chosePull ? 'pull the lever' : 'do nothing'}
              </span>
              .
            </p>

            {thisCrowd?.state === 'pending' && (
              <p className="text-sm text-textGray">Counting the crowd…</p>
            )}

            {thisCrowd?.state === 'failed' && (
              <p className="text-sm text-textGray">
                Could not reach the crowd for this one.
              </p>
            )}

            {thisCrowd?.state === 'ready' && (
              <>
                <div className="flex flex-col gap-2">
                  {[
                    {
                      label: 'Pulled the lever',
                      share: thisCrowd.pulledPercent
                    },
                    {
                      label: 'Did nothing',
                      share: 100 - thisCrowd.pulledPercent
                    }
                  ].map((row) => (
                    <div key={row.label}>
                      <div className="mb-1 flex justify-between text-xs text-textSecondary">
                        <span>{row.label}</span>
                        <span className="tabular-nums">{row.share}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-background-light">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-500 ease-in-out"
                          style={{ width: `${row.share}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sample size stated plainly. Early on it is tiny, and hiding
                 * that would make two votes look like a consensus. */}
                <p className="mt-2 text-xs text-textGray">
                  {thisCrowd.total === 1
                    ? 'You are the first person to answer this one.'
                    : `Based on ${thisCrowd.total.toLocaleString()} answers, including yours.`}
                </p>
              </>
            )}

            <p className="mt-4 text-sm italic text-textGray">
              {current.verdict}
            </p>

            <button
              type="button"
              onClick={next}
              className="mt-6 rounded-full bg-primary px-6 py-2.5 font-emphasis text-white transition-colors hover:bg-indigo-400"
            >
              {index + 1 >= DILEMMAS.length ? 'See the tally' : 'Next dilemma'}
            </button>
          </div>
        )}
      </div>

      {best !== null && (
        <p className="mt-4 text-sm text-textGray">
          Most levers you have pulled in one run: {best}/{DILEMMAS.length}.
        </p>
      )}
    </div>
  );
};

export default Dilemmas;
